import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  Grid,
  Chip,
  CircularProgress,
  SelectChangeEvent,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  Print as PrintIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Sync as SyncIcon,
  CloudDone as CloudDoneIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { printService } from '../../printServices/PrintService';

export interface Printer {
  id: string;
  name: string;
  type: 'network' | 'local' | 'bluetooth';
  address?: string;
  port?: number;
  driver?: string;
  isDefault?: boolean;
  status?: 'online' | 'offline' | 'error';
  connected?: boolean;
  paperWidth?: number; // in inches
  paperHeight?: number; // in inches
  dpi?: number;
  manufacturer?: string;
  model?: string;
  agentId?: string; // ID of the Print Agent this printer is associated with
  capabilities?: {
    color?: boolean;
    duplex?: boolean;
    supportedSizes?: string[];
  };
}

interface PrinterManagerProps {
  printers: Printer[];
  onAddPrinter: (printer: Printer) => void;
  onUpdatePrinter: (printer: Printer) => void;
  onDeletePrinter: (printerId: string) => void;
  onSetDefaultPrinter: (printerId: string) => void;
  onTestPrinter?: (printerId: string) => Promise<boolean>;
}

const PrinterManager: React.FC<PrinterManagerProps> = ({
  printers,
  onAddPrinter,
  onUpdatePrinter,
  onDeletePrinter,
  onSetDefaultPrinter,
  onTestPrinter
}) => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState<Printer | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [testingPrinter, setTestingPrinter] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{id: string, success: boolean} | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [agentPrinters, setAgentPrinters] = useState<Printer[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [cloudConnected, setCloudConnected] = useState<boolean>(false);
  
  const initialPrinterState: Printer = {
    id: '',
    name: '',
    type: 'network',
    status: 'offline',
    connected: false,
    dpi: 203
  };
  
  const [printerForm, setPrinterForm] = useState<Printer>(initialPrinterState);
  
  // Reset form when dialog closes
  useEffect(() => {
    if (!dialogOpen) {
      setFormError(null);
      setTestResult(null);
    }
  }, [dialogOpen]);
  
  // Load print agent printers on component mount
  useEffect(() => {
    loadPrintAgentPrinters();
  }, []);
  
  // Load printers from print agent
  const loadPrintAgentPrinters = () => {
    try {
      const storedPrinters = localStorage.getItem('print_agent_printers');
      if (storedPrinters) {
        const parsedPrinters = JSON.parse(storedPrinters);
        setAgentPrinters(parsedPrinters);
      }
    } catch (error) {
      console.error('Failed to load print agent printers:', error);
    }
  };
  
  // Handle opening the dialog for adding a new printer
  const handleAddPrinter = () => {
    setPrinterForm({
      ...initialPrinterState,
      id: `printer_${Date.now()}`
    });
    setEditingPrinter(null);
    setDialogOpen(true);
  };
  
  // Handle opening the dialog for editing a printer
  const handleEditPrinter = (printer: Printer) => {
    // Create a clean copy of the printer with explicit default values for address
    const printerWithDefaults = {
      ...printer,
      address: printer.address || '',  // Ensure address is never undefined
      port: printer.port || 9100
    };
    
    // Log the printer data for debugging
    console.log('Editing printer:', printerWithDefaults);
    
    // Set the form data
    setPrinterForm(printerWithDefaults);
    setEditingPrinter(printer);
    setDialogOpen(true);
  };
  
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    if (name) {
      setPrinterForm(prev => ({ ...prev, [name]: value }));
    }
  };
  
  // Handle select input changes
  const handleSelectChange = (e: SelectChangeEvent<'network' | 'local' | 'bluetooth'>) => {
    const { name, value } = e.target;
    if (name) {
      setPrinterForm(prev => ({ ...prev, [name]: value }));
    }
  };
  
  // Handle form toggle changes
  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    if (name) {
      setPrinterForm(prev => ({ 
        ...prev, 
        capabilities: {
          ...prev.capabilities,
          [name]: checked
        } 
      }));
    }
  };
  
  // Handle saving the printer
  const handleSavePrinter = () => {
    // Validate form
    if (!printerForm.name) {
      setFormError('Printer name is required');
      return;
    }
    
    if (printerForm.type === 'network' && !printerForm.address) {
      setFormError('IP address is required for network printers');
      return;
    }
    
    // Save printer to local storage as well (for offline/permissions backup)
    try {
      const storedPrinters = JSON.parse(localStorage.getItem('printers') || '[]');
      
      // Update or add the printer
      if (editingPrinter) {
        const index = storedPrinters.findIndex((p: Printer) => p.id === printerForm.id);
        if (index >= 0) {
          storedPrinters[index] = printerForm;
        } else {
          storedPrinters.push(printerForm);
        }
      } else {
        storedPrinters.push(printerForm);
      }
      
      localStorage.setItem('printers', JSON.stringify(storedPrinters));
    } catch (localStorageError) {
      console.warn('Failed to save printer to localStorage:', localStorageError);
    }
    
    // Save printer to Firestore
    if (editingPrinter) {
      onUpdatePrinter(printerForm);
    } else {
      onAddPrinter(printerForm);
    }
    
    // Close dialog
    setDialogOpen(false);
  };
  
  // Handle deleting a printer
  const handleDeletePrinter = (printerId: string) => {
    if (window.confirm('Are you sure you want to delete this printer?')) {
      onDeletePrinter(printerId);
    }
  };
  
  // Handle testing a printer
  const handleTestPrinter = async (printerId: string): Promise<boolean> => {
    if (!onTestPrinter) return false;
    
    setTestingPrinter(printerId);
    try {
      // Find the printer to test
      const printerToTest = printers.find(p => p.id === printerId);
      if (!printerToTest) throw new Error('Printer not found');
      
      // Use our print service to send a test page
      const success = await printService.testPrinter(printerToTest);
      setTestResult({ id: printerId, success });
      return success;
    } catch (error) {
      console.error('Test printer error:', error);
      setTestResult({ id: printerId, success: false });
      return false;
    } finally {
      setTestingPrinter(null);
    }
  };
  
  // Handle refreshing printer status and syncing printers
  const handleRefreshPrinters = async () => {
    setRefreshing(true);
    setFormError(null);
    
    try {
      // Check print agent and refresh printer status
      const agentConfig = printService.getPrintAgentConfig();
      const agentAvailable = await printService.checkLocalPrintAgent(agentConfig.hostname, agentConfig.port);
      
      // Reload printers from print agent
      loadPrintAgentPrinters();
      
      if (!agentAvailable) {
        // If Print Agent is not available, show a helpful message
        setFormError('Print Agent is not running or not accessible. Make sure it\'s installed and running on your computer.');
        return;
      }
      
      // Sync printers from agent to TendCloud
      const result = await printService.syncPrintersFromAgent();
      
      if (result.success && result.printers && result.printers.length > 0) {
        // Add all new printers to the UI state
        result.printers.forEach(printer => {
          onAddPrinter(printer);
        });
        
        // Show success message
        setSuccessMessage(`Synchronized ${result.printers.length} printer(s) from Print Agent to TendCloud.`);
      } else if (result.success && (!result.printers || result.printers.length === 0)) {
        // No printers were found to sync
        setFormError('No new printers found to sync. Check that your printers are connected properly.');
      } else {
        // Sync failed
        setFormError(result.message || 'Failed to sync printers from Print Agent.');
      }
    } catch (error) {
      console.error('Error refreshing printers:', error);
      setFormError('An error occurred while refreshing printer information. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };
  
  // Add this useEffect hook to check cloud connectivity
  useEffect(() => {
    const checkCloudConnection = async () => {
      try {
        // Try to connect to the listPrintAgents endpoint
        const response = await fetch('https://us-central1-fllboats.cloudfunctions.net/listPrintAgents');
        const data = await response.json();
        
        // Even if there are no agents, if we get a valid response, the cloud is connected
        if (response.ok || data.success === false) {
          setCloudConnected(true);
        }
      } catch (error) {
        console.error('Error checking cloud connectivity:', error);
        setCloudConnected(false);
      }
    };
    
    checkCloudConnection();
  }, []);
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Printer Management</Typography>
        <Box>
          {/* Add cloud connectivity indicator */}
          {cloudConnected && (
            <Tooltip title="Cloud Functions Connected">
              <Chip
                icon={<CloudDoneIcon />}
                label="Cloud Ready"
                color="success"
                size="small"
                sx={{ mr: 2 }}
              />
            </Tooltip>
          )}
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            onClick={handleRefreshPrinters}
            disabled={refreshing}
            sx={{ mr: 1 }}
          >
            {refreshing ? <CircularProgress size={20} /> : 'Refresh & Sync'}
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={handleAddPrinter}
          >
            Add Printer
          </Button>
        </Box>
      </Box>
      {/* Display message if printers are available in print agent but not in Firestore */}
      {printers.length === 0 && agentPrinters.length > 0 && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          key="sync-alert"
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleRefreshPrinters}
              disabled={refreshing}
            >
              {refreshing ? <CircularProgress size={20} /> : 'SYNC NOW'}
            </Button>
          }
        >
          {agentPrinters.length} printer(s) detected in Print Agent but not synced to TendCloud.
          Click SYNC NOW to add them to your TendCloud database.
        </Alert>
      )}
      <Paper>
        {formError && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }} 
            onClose={() => setFormError(null)}
            key="paper-error-alert"
            action={
              formError.includes('Print Agent') && (
                <Button
                  color="inherit"
                  size="small"
                  href="https://fllboats.web.app/print-agent"
                  target="_blank"
                  key="download-agent-btn"
                >
                  Download Print Agent
                </Button>
              )
            }
          >
            {formError}
          </Alert>
        )}
        
        {printers.length === 0 && (
          <Alert 
            severity="info" 
            sx={{ mb: 2 }} 
            key="cloud-functions-info"
          >
            <Typography variant="subtitle2" gutterBottom>
              About Print Agent Communication
            </Typography>
            <Typography variant="body2">
              The system uses Firebase Cloud Functions to communicate with Print Agents, which means:
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Print Agents can be on any computer with internet access</li>
              <li>No port forwarding or firewall configuration is needed</li>
              <li>Communication is secure and reliable even across different networks</li>
            </ul>
            <Typography variant="body2">
              For optimal experience, make sure the Print Agent is running on at least one computer in your organization.
            </Typography>
          </Alert>
        )}
        
        {printers.length > 0 ? (
          <List>
            {printers.map((printer) => (
              <React.Fragment key={printer.id}>
                <ListItem>
                  <ListItemIcon>
                    <PrintIcon color={printer.status === 'online' ? 'primary' : 'disabled'} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1" component="span">
                          {printer.name}
                        </Typography>
                        {printer.isDefault && (
                          <Chip
                            label="Default"
                            size="small"
                            color="primary"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" component="span">
                          {printer.type === 'network' ? `${printer.address}:${printer.port || 9100}` : printer.type}
                        </Typography>
                        {printer.manufacturer && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {printer.manufacturer} {printer.model || ''}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!printer.isDefault && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => onSetDefaultPrinter(printer.id)}
                          startIcon={<CheckIcon />}
                        >
                          Set Default
                        </Button>
                      )}
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() => handleEditPrinter(printer)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeletePrinter(printer.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="test"
                        onClick={() => handleTestPrinter(printer.id)}
                        disabled={testingPrinter === printer.id}
                      >
                        {testingPrinter === printer.id ? (
                          <CircularProgress size={24} />
                        ) : (
                          <PrintIcon />
                        )}
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              No printers configured
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add a printer to start printing labels. You can add network printers, Print Agent printers, or system printers.
            </Typography>
            
            <Box sx={{ mb: 3, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
              <Typography variant="body2" color="info.dark" sx={{ mb: 1 }}>
                <strong>TIP:</strong> For the best printing experience, install the Print Agent application.
              </Typography>
              <Typography variant="body2" color="info.dark" sx={{ mb: 2 }}>
                Print Agent automatically discovers your printers and keeps them in sync with the FLL Marina system.
              </Typography>
              <Typography variant="caption" color="info.dark" sx={{ display: 'block', mb: 2 }}>
                The new outbound-only print agent works on any network without port forwarding requirements.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="info"
                href="https://fllboats.web.app/print-agent"
                target="_blank"
                sx={{ mr: 2 }}
              >
                Download Print Agent
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="info"
                onClick={() => navigate('/staff/store/labels/agents')}
                sx={{ mr: 2 }}
              >
                Manage Print Agents
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="info"
                onClick={handleRefreshPrinters}
                disabled={refreshing}
              >
                {refreshing ? <CircularProgress size={20} /> : 'Check for Print Agent'}
              </Button>
            </Box>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddPrinter}
            >
              Add Printer Manually
            </Button>
          </Box>
        )}
        
        {printers.length > 0 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }} key="add-printer-box">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddPrinter}
            >
              Add Printer
            </Button>
          </Box>
        )}
      </Paper>
      {/* Success Message Snackbar */}
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" key="success-alert">
          {successMessage}
        </Alert>
      </Snackbar>
      {/* Add/Edit Printer Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPrinter ? 'Edit Printer' : 'Add New Printer'}
        </DialogTitle>
        <DialogContent dividers>
          {formError && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }} 
              onClose={() => setFormError(null)}
              key="form-error-alert"
            >
              {formError}
            </Alert>
          )}
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Printer Name"
                name="name"
                value={printerForm.name}
                onChange={handleInputChange}
                fullWidth
                required
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Printer Type</InputLabel>
                <Select
                  name="type"
                  value={printerForm.type}
                  label="Printer Type"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="network" key="network">Network Printer</MenuItem>
                  <MenuItem value="local" key="local">Local Printer</MenuItem>
                  <MenuItem value="bluetooth" key="bluetooth">Bluetooth Printer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {printerForm.type === 'network' && (
              <>
                <Grid item xs={12} md={6} key="address-field">
                  <TextField
                    label="IP Address"
                    name="address"
                    value={printerForm.address || ''}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    margin="normal"
                  />
                </Grid>
                
                <Grid item xs={12} md={6} key="port-field">
                  <TextField
                    label="Port"
                    name="port"
                    type="number"
                    value={printerForm.port || 9100}
                    onChange={handleInputChange}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">Printer Specifications</Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                label="DPI (Dots Per Inch)"
                name="dpi"
                type="number"
                value={printerForm.dpi || 203}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                helperText="Common values: 203, 300, 600"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                label="Paper Width (inches)"
                name="paperWidth"
                type="number"
                value={printerForm.paperWidth || 4}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.1 }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                label="Paper Height (inches)"
                name="paperHeight"
                type="number"
                value={printerForm.paperHeight || 6}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.1 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">Printer Capabilities</Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    name="color"
                    checked={!!printerForm.capabilities?.color}
                    onChange={handleToggleChange}
                  />
                }
                label="Color Printing"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    name="duplex"
                    checked={!!printerForm.capabilities?.duplex}
                    onChange={handleToggleChange}
                  />
                }
                label="Duplex (Double-sided) Printing"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSavePrinter}
          >
            {editingPrinter ? 'Update Printer' : 'Add Printer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrinterManager; 